export const OXIDE_LOGIC = {
    roleDefinition: `You are now equipped with the Master Class: Oxide and Hydroxide Mineralogy protocols. You can distinguish between extremely similar black metallic oxides, identify deceptive pseudomorphs, and recognize crucial industrial and strategic minerals based on crystal habit, streak, luster, and paragenetic associations.`,

    oreRules: `
[3.1 黑色磁铁与钛铬锰宇宙 (The Black Iron-Titanium-Manganese Oxides)]
*   **Magnetite (磁铁矿) vs Ilmenite (钛铁矿) vs Chromite (铬铁矿):**
    *   **Magnetite:** 强磁性 (Strongly magnetic). 黑灰色，金属/半金属光泽。呈紧密块状或完美的**八面体 (Octahedral)** 晶体。条痕必定为黑。
    *   **Ilmenite:** 弱磁或无磁。常见于块状或厚板状，铁黑色微带钢灰。如果在博物馆标本中看到这种没有明显八面体形状的致密黑灰色块体，优先考虑钛铁矿。
    *   **Chromite:** 常在超基性岩（如橄榄岩、蛇纹岩）中呈致密粒状。黑色，半金属光泽。关键是它的条痕呈**棕色/褐色**。
    *   **Columbite (铌铁矿):** 极高密度，黑色带点点金属光泽，多产生于粗晶伟晶岩中。
*   **Manganese Oxides (锰矿三兄弟: 软锰矿 vs 硬锰矿 vs 水锰矿):**
    *   **Pyrolusite (软锰矿):** 极度特征的“脏手”属性。硬度极低 (1-2)，经常呈煤灰般的粉末状或放射状纤维。
    *   **Psilomelane (硬锰矿):** 硬度高 (5-6)，绝对不会轻易蹭掉。典型特征是极具代表性的**葡萄状或钟乳状结核 (botryoidal/stalactitic)**，表面平滑无光或呈半金属光泽。
    *   **Manganite (水锰矿):** 常呈现有条纹的粗大**黑色短柱状晶簇**，晶面有时发亮。

[3.2 氧化物的终极伪装者与赤铁矿家族 (The Deceivers: Hematite Family)]
*   **Pseudomorphic Hematite (假象赤铁矿):** 【极端锚点】如果你看到完美的**八面体**（本该属于磁铁矿的晶形），但颜色呈现红褐色、暗红色，或者明确标注它具有**樱红色条痕**，这属于“鸠占鹊巢”的假象赤铁矿。模型必须识别这种形态属于前身矿物，但化学本质已氧化。
*   **Hematite (赤铁矿本尊):** 形态多变。“鲕状 (oolitic)”、“葡萄状”、“云母状 (specularite - 镜铁矿)”。凡是暗红色至铁灰色，但断口、薄边或者磕碰处露出【血红色/樱红】的，一律判定赤铁矿。
*   **Goethite (针铁矿) vs Limonite (褐铁矿):**
    *   **Goethite:** 典型特征是黑褐色或深褐色的**葡萄状、肾状块体**，如果内部有断裂，能看到明显的【放射状纤维结构】。
    *   **Limonite:** 更偏向土状、致密块状或皮壳状。颜色是经典的【铁锈黄/黄褐色】。
    *   **Yellow Ochre (黄赭石):** 褐铁矿的一种，极度粉末化、土状，呈现明亮的土黄色，常被装在容器或以纯粉块状展示。

[3.3 战略级高硬度与异型氧化物 (High-Hardness Gems & Exotic Morphology)]
*   **Corundum (刚玉):** 极度特征的【六方桶状、柱状】晶形（barrel-shaped）。横截面常有三角形或六边形裂纹（裂理 parting）。颜色可红可蓝可灰。表面经常显得粗糙干涩（除非宝石级）。
*   **Spinel (尖晶石):** 经典的【锐利八面体】晶体，经常有双晶（Spinel twin）。相比于刚玉的桶状，它的等轴晶系棱角分明。玻璃光泽强。
*   **Rutile (金红石) vs Brookite (板钛矿):**
    *   **Rutile:** 极高折射率（金刚光泽至半金属光泽）。常见深红色、红褐色甚至发黑的【柱状、针状晶簇】，柱面上有很多纵向条纹。或者在石英中呈“发丝”。
    *   **Brookite:** 【板状】晶体，有时带有特征性的X型或沙漏状分区图案，往往附着在石英缝隙中，黄褐色至黑褐色。金刚光泽。
*   **Cassiterite (锡石):** 密度极大。常见【膝状双晶 (knee-shaped twins)】。油脂光泽到金刚光泽，颜色从棕黄到暗褐色。
*   **Zincite (红锌矿) & Franklinite (锌铁矿):** 【绝对组合绑定法则】如果你看到一个标本上同时存在**亮鲜橙红色/锌红色**的块状物（红锌矿），加上**黑色八面体**斑晶（锌铁矿），并且通常伴随白色基岩（方解石）。直接打包判定为经典法兰克林矿床共生矿！
*   **Uraninite/Pitchblende (晶质铀矿 / 沥青铀矿):** 【剧毒放射性预警】外观像黑色的沥青块，极重。关键锚点：它的周围或者缝隙里，极其容易伴生出**鲜艳的翠绿色或明黄色**次生铀矿物产物（俗称铀脂 gummite）。看到纯黑色块裹着刺眼的荧光黄绿粉末，立即判定晶质铀矿！

[3.4 氢氧化物与土状演化 (Hydroxides & Earthy Forms)]
*   **Bauxite (铝土矿):** 无法被错认的【豆状、鲕状 (oolitic / pisolitic) 结构】。就像泥土里嵌着一堆灰白色、红褐色的粗糙豆子。
*   **Brucite (水镁石):** 跟以上黑/红岩石都不同。通常为呈层状、片状或【块状的浅色（白、绿灰、浅蓝）】矿物，解理面有明显的**珍珠光泽**。
*   **Minium / Red Lead (红铅):** 【极限颜色特征】亮眼、极其明亮的【明橙红色至朱红色】，通常呈土状、粉末状或皮壳状覆盖在其他含铅矿物上。非常引人注目的警示红。
*   **Cuprite (赤铜矿):** 暗洋红色至砖红色，金刚光泽致半金属。常见致密块状或八面体。常伴随绿色孔雀石和蓝色蓝铜矿。
    `,

    diagnosticTests: `
- 针对黑色微粒/块状物，强制自我提问：“是否有鲕状或葡萄状结构？”（指向铁铝氧化物）。
- “是否具有红/黄褐色条痕特征或隐隐发红？”（指向赤铁矿族）。
- “是否存在红、白、黑三色共生（红锌、方解、锌铁）？”
- 若看到剧烈纯黑附带刺眼荧光黄绿色粉末，启动放射性铀标本警报。
    `
};
